import { useState, useRef, useMemo } from 'react'

import {
  Box,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  Avatar,
  Typography,
  Popper,
  Paper,
} from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { NavLink, useLocation } from 'react-router-dom'

import { useLanguage } from '../../i18n'

import BranchSwitcher from './BranchSwitcher'
import { useNavigationConfig } from './navigationConfig'

function Sidebar({ collapsed }) {
  const theme = useTheme()
  const location = useLocation()
  const { t } = useLanguage()
  const navigationConfig = useNavigationConfig()
  const [openSections, setOpenSections] = useState({ company: true })
  const [hoveredItem, setHoveredItem] = useState(null)
  const [anchorEl, setAnchorEl] = useState(null)
  const popperRef = useRef(null)
  const leaveTimerRef = useRef(null)

  const popperOpen = Boolean(anchorEl) && collapsed
  const hoveredNavItem = navigationConfig.find((item) => item.id === hoveredItem)

  const activeParentId = useMemo(() => {
    for (const item of navigationConfig) {
      if (item.subItems?.some((sub) => location.pathname.startsWith(sub.path))) {
        return item.id
      }
    }
    return null
  }, [location.pathname, navigationConfig])

  const effectiveOpenSections = useMemo(() => {
    if (activeParentId) return { ...openSections, [activeParentId]: true }
    return openSections
  }, [openSections, activeParentId])

  // const sidebarWidth = collapsed ? collapsedWidth : drawerWidth

  const toggleSection = (section) => {
    setOpenSections((prev) => ({ ...prev, [section]: !prev[section] }))
  }

  const handleMouseEnter = (event, item) => {
    if (collapsed) {
      // Clear any pending close
      if (leaveTimerRef.current) {
        clearTimeout(leaveTimerRef.current)
        leaveTimerRef.current = null
      }
      setAnchorEl(event.currentTarget)
      setHoveredItem(item.id)
    }
  }

  const handleMouseLeave = () => {
    if (collapsed) {
      // Delay close to allow moving to popper
      leaveTimerRef.current = setTimeout(() => {
        setAnchorEl(null)
        setHoveredItem(null)
      }, 150)
    }
  }

  const handlePopperMouseEnter = () => {
    // Cancel pending close
    if (leaveTimerRef.current) {
      clearTimeout(leaveTimerRef.current)
      leaveTimerRef.current = null
    }
    // Keep anchorEl as-is — don't reassign
  }

  const handlePopperMouseLeave = () => {
    if (collapsed) {
      leaveTimerRef.current = setTimeout(() => {
        setAnchorEl(null)
        setHoveredItem(null)
      }, 150)
    }
  }

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        p: 0,
        bgcolor: 'var(--sidebar-bg)',
        color: 'var(--sidebar-text)',
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <div className="flex items-center gap-2 p-3 logo-section">
        <Avatar
          sx={{ bgcolor: theme.palette.primary.main, width: 44, height: 44, fontWeight: 700 }}
        >
          MD
        </Avatar>
        {!collapsed && (
          <Box>
            <Typography variant="h6" fontWeight={700} color="var(--sidebar-text)">
              MDG Guardian
            </Typography>
            <Typography
              variant="caption"
              color="var(--sidebar-text-muted)"
              sx={{ display: 'block' }}
            >
              {t('SaaS admin platform')}
            </Typography>
          </Box>
        )}
      </div>

      <BranchSwitcher collapsed={collapsed} />
      {/* Scrollable Navigation */}
      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          overflowX: 'hidden',
          '&::-webkit-scrollbar': {
            width: '4px',
          },
          '&::-webkit-scrollbar-track': {
            background: 'transparent',
          },
          '&::-webkit-scrollbar-thumb': {
            background: 'var(--scrollbar-thumb)',
            borderRadius: '4px',
          },
        }}
      >
        <List sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, py: 0 }}>
          {navigationConfig.map((item) => {
            const hasChildren = item.subItems?.length > 0
            const isParentActive = activeParentId === item.id
            const active = item.path ? false : isParentActive
            // Wrapper for hover + popper
            const navItem = (
              <Box
                key={item.id}
                sx={{ position: 'relative' }}
                onMouseEnter={(e) => handleMouseEnter(e, item)}
                onMouseLeave={handleMouseLeave}
              >
                <ListItemButton
                  component={item.path ? NavLink : 'button'}
                  to={item.path || undefined}
                  className={`cstm_nav_item gap-2 !rounded-none w-full !m-0 ${active ? 'active' : ''}`}
                  onClick={item.subItems ? () => toggleSection(item.id) : undefined}
                  sx={{
                    px: collapsed ? 0.75 : 2,
                    py: 1.3,
                    justifyContent: collapsed ? 'center' : 'flex-start',
                    color: 'var(--sidebar-text-muted)',
                  }}
                >
                  <div className="nav-icon">{item.icon && <item.icon size={16} />}</div>
                  {!collapsed && (
                    <ListItemText
                      className="!m-0"
                      primary={item.label}
                      primaryTypographyProps={{
                        fontWeight: 400,
                        color: 'inherit',
                        fontSize: '0.875rem',
                      }}
                    />
                  )}
                  {!collapsed && hasChildren && (
                    <Typography
                      color="var(--sidebar-text-muted)"
                      variant="body2"
                      sx={{ fontSize: '0.875rem' }}
                    >
                      {effectiveOpenSections[item.id] ? '▾' : '▸'}
                    </Typography>
                  )}
                </ListItemButton>

                {/* Expanded submenu (not collapsed) */}
                {hasChildren && !collapsed && (
                  <Collapse in={effectiveOpenSections[item.id]} timeout="auto" unmountOnExit>
                    <List component="div" disablePadding sx={{ ml: 3, mt: 0.5, gap: 0.5 }}>
                      {item.subItems.map((subItem) => (
                        <ListItemButton
                          key={subItem.id}
                          component={NavLink}
                          to={subItem.path}
                          className={({ isActive }) =>
                            `${effectiveOpenSections[item.id] ? 'cstm_nav_item' : ''}${isActive ? ' active' : ''}`
                          }
                          sx={{
                            borderRadius: 0,
                            px: 2,
                            py: 1.1,
                            color: 'var(--sidebar-text-muted)',
                            '&.active': {
                              bgcolor: 'var(--sidebar-active-bg)',
                            },
                          }}
                        >
                          {subItem.icon && (
                            <ListItemIcon
                              sx={{
                                minWidth: 26,
                                color: 'inherit',
                                display: 'inline-flex',
                                alignItems: 'center',
                              }}
                            >
                              <subItem.icon size={14} />
                            </ListItemIcon>
                          )}
                          <ListItemText
                            primary={subItem.label}
                            primaryTypographyProps={{
                              fontSize: '0.88rem',
                              color: 'inherit',
                              fontWeight: 400,
                            }}
                          />
                        </ListItemButton>
                      ))}
                    </List>
                  </Collapse>
                )}
              </Box>
            )

            return navItem
          })}
        </List>
      </Box>

      {/* Hover popup for collapsed state with children */}
      <Popper
        ref={popperRef}
        open={popperOpen}
        anchorEl={anchorEl}
        placement="right-start"
        sx={{ zIndex: 1300 }}
      >
        <Paper
          onMouseEnter={handlePopperMouseEnter}
          onMouseLeave={handlePopperMouseLeave}
          sx={{
            bgcolor: 'var(--sidebar-bg)',
            border: '1px solid var(--sidebar-border)',
            borderRadius: 0,
            minWidth: 180,
            py: 1,
            ml: 0,
          }}
        >
          {hoveredNavItem && (
            <>
              <Typography
                variant="overline"
                sx={{
                  px: 2,
                  py: 0.5,
                  color: 'var(--sidebar-text-muted)',
                  display: 'block',
                  fontSize: '0.7rem',
                }}
              >
                {hoveredNavItem.label}
              </Typography>
              {/* {hoveredNavItem.path && (
                <ListItemButton
                  component={NavLink}
                  to={hoveredNavItem.path}
                  sx={{
                    borderRadius: 1,
                    mx: 1,
                    color: 'var(--sidebar-text)',
                    fontWeight: 600,
                    fontSize: '0.9rem',
                    '&:hover': {
                      bgcolor: 'var(--sidebar-hover-bg)'
                    }
                  }}
                >
                  {hoveredNavItem.label}
                </ListItemButton>
              )} */}
              {hoveredNavItem.subItems?.map((subItem) => (
                <ListItemButton
                  key={subItem.id}
                  component={NavLink}
                  to={subItem.path}
                  className={({ isActive }) => (isActive ? 'active' : '')}
                  sx={{
                    borderRadius: 1,
                    mx: 1,
                    color: 'var(--sidebar-text-muted)',
                    '&.active': {
                      bgcolor: 'var(--sidebar-active-bg)',
                    },
                  }}
                >
                  <ListItemText
                    primary={subItem.label}
                    primaryTypographyProps={{
                      fontSize: '0.85rem',
                      fontWeight: 500,
                    }}
                  />
                </ListItemButton>
              ))}
            </>
          )}
        </Paper>
      </Popper>
    </Box>
  )
}

export { Sidebar }
export default Sidebar
